// Console implementation of the `@formant/chat` incidents adapter (the DATA seam
// for incidents). The package owns the incidents UI/UX; the console owns the
// data — an incident IS a workflow `Run`, fetched from the cortex API and typed
// by @cortex/types, which the package is forbidden from importing. This adapter
// wraps the existing run/node-execution query hooks + the attach/detach endpoints
// and hands them to <ChatProvider incidentsAdapter={…}>, lighting up incidents on
// BOTH chat surfaces (the /chat rail + the floating launcher drawer).
import { useClient } from '@cortex/react-query';
import type { IIncidentsAdapter } from '@formant/chat';
import { useMemo } from 'react';

import {
  attachIncidentContextToThread,
  detachIncidentContextFromThread,
} from './incidents/incident-context-hook';
import {
  useIncidentNodeExecutions,
  useIncidentRun,
  useIncidentRuns,
} from './incidents/use-incidents';

/**
 * Build the incidents adapter for a chat group. The `use*` members are the
 * console's react-query hooks passed through verbatim — the package calls them
 * during render (they already read `useClient()` internally). The result is
 * memoized on `[client, groupId]` so the adapter object stays referentially
 * stable, as the package requires (its hook call order must never change).
 *
 * The console `Run` / `NodeExecution` models are a structural superset of the
 * package's incident types, so the hooks satisfy the adapter signature with no
 * mapping.
 */
export function useConsoleIncidentsAdapter(groupId: string): IIncidentsAdapter {
  const client = useClient();
  return useMemo<IIncidentsAdapter>(
    () => ({
      useIncidentRuns,
      useIncidentRun,
      useIncidentNodeExecutions,
      attachIncident: (threadId: string, runId: string) =>
        attachIncidentContextToThread(client.http, groupId, { threadId, runId }),
      detachIncident: (threadId: string, runId: string) =>
        detachIncidentContextFromThread(client.http, groupId, { threadId, runId }),
    }),
    [client, groupId],
  );
}
